'use client';

import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { IconToggleGroup } from '@/components';
import { FOOTER_LINK_TABS, type FooterLinkTab, type FooterLinkTabOption } from '@/constants';
import {
  ChevronIcon,
  Column,
  ColumnSection,
  ColumnTitle,
  ColumnsGrid,
  LinkBoxContainer,
  LinkBoxWrapper,
  LinkItem,
  LinkList,
  SeoLinkStyled,
  TabsWrapper,
} from './styles';

type FooterLinkBoxProps = {
  tabs?: FooterLinkTab[];
};

export const FooterLinkBox: FC<FooterLinkBoxProps> = ({ tabs = FOOTER_LINK_TABS }) => {
  const t = useTranslations();

  const resolveLabel = (label: string, labelValues?: Record<string, string>) => {
    if (!t.has(label)) return label;
    if (!labelValues) return t(label);

    const values = Object.fromEntries(
      Object.entries(labelValues).map(([key, value]) => [key, t.has(value) ? t(value) : value]),
    );

    return t(label, values);
  };

  const tabOptions = useMemo<FooterLinkTabOption[]>(
    () =>
      tabs.map(({ id, labelKey }) => ({
        id,
        label: resolveLabel(labelKey),
      })),
    [tabs, t],
  );

  const [activeTab, setActiveTab] = useState<FooterLinkTabOption>(tabOptions[0]);

  useEffect(() => {
    setActiveTab(tabOptions[0]);
  }, [tabOptions]);

  const activeTabData = useMemo(
    () => tabs.find((tab) => tab.id === activeTab.id) ?? tabs[0],
    [activeTab.id, tabs],
  );

  const gridColumnCount = useMemo(
    () => Math.max(...tabs.map((tab) => tab.columns.length), 1),
    [tabs],
  );

  const renderLinkItems = (items: FooterLinkTab['columns'][number]['items'], keyPrefix: string) =>
    items.map((item) => {
      const label = resolveLabel(item.label, item.labelValues);
      return (
        <LinkItem key={`${keyPrefix}-${item.href}-${item.label}`}>
          <ChevronIcon aria-hidden="true">&rsaquo;</ChevronIcon>
          <SeoLinkStyled href={item.href}>{label}</SeoLinkStyled>
        </LinkItem>
      );
    });

  const handleTabChange = (value: FooterLinkTabOption[]) => {
    if (value.length === 0) return;
    setActiveTab(value[value.length - 1]);
  };

  if (!activeTabData) return null;

  return (
    <LinkBoxWrapper data-testid="footer-linkbox">
      <LinkBoxContainer>
        <TabsWrapper role="tablist" aria-label="Footer navigation">
          <IconToggleGroup
            equalWidth
            hideDefaultIcon
            sx={{
              '@media (max-width: 768px)': {
                gridTemplateColumns: '1fr !important',
              },
            }}
            options={tabOptions.map((tab) => ({
              value: tab,
              title: tab.label,
            }))}
            selected={[activeTab]}
            onChange={handleTabChange}
            isEqual={(a, b) => a.id === b.id}
          />
        </TabsWrapper>

        {tabs.map((tab) => {
          const isActive = tab.id === activeTab.id;

          return (
            <ColumnsGrid
              key={tab.id}
              role="tabpanel"
              id={`footer-tab-${tab.id}`}
              aria-labelledby={`footer-tab-${tab.id}`}
              aria-hidden={!isActive}
              $columnCount={tab.columnCount ?? gridColumnCount}
              $hidden={!isActive}
            >
              {tab.columns.map((column, columnIndex) => (
                <Column key={`${tab.id}-column-${columnIndex}`}>
                  {column.sections?.length ? (
                    column.sections.map((section, sectionIndex) => (
                      <ColumnSection
                        key={`${tab.id}-column-${columnIndex}-section-${sectionIndex}`}
                      >
                        {section.title ? (
                          <ColumnTitle>{resolveLabel(section.title)}</ColumnTitle>
                        ) : null}
                        <LinkList>
                          {renderLinkItems(
                            section.items,
                            `${tab.id}-column-${columnIndex}-section-${sectionIndex}`,
                          )}
                        </LinkList>
                      </ColumnSection>
                    ))
                  ) : (
                    <>
                      {column.title ? (
                        <ColumnTitle>{resolveLabel(column.title)}</ColumnTitle>
                      ) : null}
                      <LinkList>
                        {renderLinkItems(column.items, `${tab.id}-column-${columnIndex}`)}
                      </LinkList>
                    </>
                  )}
                </Column>
              ))}
            </ColumnsGrid>
          );
        })}
      </LinkBoxContainer>
    </LinkBoxWrapper>
  );
};
